/**
 * La scadenza del risultato dal gioco di un incontro `provider` (kb#343, qTournaments 1.111+, `limulo#206`):
 * `resultDeadlineAt` sulla riga dell'incontro, in `tournament.match.list` e `.read`.
 *
 * Contratto [limulo-core-orcb 22:02Z, da gionko kb#343 21:50Z]:
 *  · ISO UTC oppure `null`;
 *  · `null` se l'incontro e' concluso, se e' passato allo staff (`resultTimedOutAt`), se l'invito e' stato
 *    rilanciato o riaperto, se non c'e' invito, e prima del primo giro del battito (60 s);
 *  · valore = finestra del torneo + 10′ (default 70′), allungato dalle pause.
 *
 * 🔴 **Conta il VALORE, mai la presenza della chiave.** Misurato il 04/10 22:02Z (consegna-tornei-orcb,
 * collaudo-c, solo GET): la chiave arriva a `null` su 17 righe su 17, e c'era gia' a `null` con qT 1.108.0
 * (la proiezione della facciata). Un valore ISO vivo NON e' ancora stato visto: questo modulo e' scritto sul
 * contratto, non su un dato.
 *
 * Il valore NON e' fidato: cio' che non e' un ISO con fuso diventa `null`, cioe' «non lo so» — la frase di prima.
 */
import { formatData, formatOra } from '../fmt.ts';

/**
 * `AAAA-MM-GGTHH:MM[:SS[.fff]]` + `Z` oppure `±HH:MM`. ⚠️ Il fuso e' OBBLIGATORIO: `Date.parse` legge
 * `2026-10-04T22:00:00` come ORA LOCALE del dispositivo, e una scadenza che cambia di ore da un telefono
 * all'altro e' peggio di nessuna.
 */
const ISO_CON_FUSO =
  /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/;

const giorniDelMese = (anno: number, mese: number): number => new Date(Date.UTC(anno, mese, 0)).getUTCDate();

/**
 * `resultDeadlineAt` fidato: un ISO UTC valido, normalizzato (`toISOString`), altrimenti `null`.
 * Assente, `null`, non stringa, non-ISO, senza fuso, `2026-02-31` (V8 lo «aggiusta» al 3 marzo): tutti `null`.
 */
export function scadenzaRisultatoOf(valore: unknown): string | null {
  if (typeof valore !== 'string') return null;
  const m = ISO_CON_FUSO.exec(valore);
  if (m === null) return null;
  const mese = Number(m[2]);
  if (mese < 1 || mese > 12 || Number(m[3]) < 1 || Number(m[3]) > giorniDelMese(Number(m[1]), mese)) return null;
  const ms = Date.parse(valore);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/**
 * Cosa dice la frase dell'attesa del gioco:
 *  · `senza`  — nessuna scadenza valida: la frase di sempre, invariata;
 *  · `entro`  — scadenza valida e STRETTAMENTE futura: «entro le HH:MM» (`altroGiorno` se non e' oggi);
 *  · `scaduta`— scadenza valida e arrivata (anche ESATTAMENTE adesso) con l'incontro ancora in attesa: la
 *               finestra fra la scadenza e il battito che lo passa allo staff. Mai un orario passato.
 */
export type FraseAttesa =
  | { kind: 'senza' }
  | { kind: 'entro'; istante: number; altroGiorno: boolean }
  | { kind: 'scaduta' };

/** `scadenza` e' un valore GIA' passato da `scadenzaRisultatoOf` (o `null`); `adesso` in ms epoch. */
export function fraseAttesaOf(scadenza: string | null | undefined, adesso: number): FraseAttesa {
  const valida = scadenzaRisultatoOf(scadenza ?? null);
  if (valida === null) return { kind: 'senza' };
  const istante = Date.parse(valida);
  if (istante <= adesso) return { kind: 'scaduta' };
  const d = new Date(istante);
  const o = new Date(adesso);
  const altroGiorno =
    d.getFullYear() !== o.getFullYear() || d.getMonth() !== o.getMonth() || d.getDate() !== o.getDate();
  return { kind: 'entro', istante, altroGiorno };
}

/**
 * I segnaposto della frase `entro`: `time` = solo ore e minuti, ora LOCALE del dispositivo nel formato della
 * lingua dell'app (`formatOra`, `lib/fmt.ts`); `day` = giorno e mese corto, solo se non e' oggi.
 */
export function segnapostiScadenza(
  frase: Extract<FraseAttesa, { kind: 'entro' }>,
  lingua: string,
): { time: string; day: string } {
  return {
    time: formatOra(frase.istante, lingua, { hour: '2-digit', minute: '2-digit' }),
    day: frase.altroGiorno ? formatData(frase.istante, { day: 'numeric', month: 'short' }, lingua) : '',
  };
}

/** Il massimo di `setTimeout` (31 bit): oltre, scatta subito. Una scadenza a >24 giorni non arma niente. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * Fra quanti ms la frase deve ridisegnarsi: alla scadenza, e basta. `null` = niente da armare (scadenza
 * assente/invalida, gia' arrivata, o oltre il massimo di un timeout).
 */
export function msAllaScadenza(scadenza: string | null | undefined, adesso: number): number | null {
  const valida = scadenzaRisultatoOf(scadenza ?? null);
  if (valida === null) return null;
  const ms = Date.parse(valida) - adesso;
  return ms > 0 && ms <= MAX_TIMEOUT_MS ? ms : null;
}
